import React from "react";
import { render } from "react-dom";
import { Provider } from "react-redux";
import { Router, Route, IndexRoute } from "react-router";
import { UserAuthWrapper } from "redux-auth-wrapper";
import { routerActions } from "react-router-redux";
import injectTapEventPlugin from "react-tap-event-plugin";
import store, { history } from "./Store";

import "../node_modules/bootstrap/dist/css/bootstrap.min.css";

injectTapEventPlugin();

import App from "./components/App";
import Login from "./components/Login";
import CardGrid from "./components/CardGrid";
import NotFound from "./components/NotFound";

// Client Components
import ClientIndex from "./components/clients/ClientIndex";
import ClientSyncIndex from "./components/clients/ClientSyncIndex";
import PendingClientUpdates from "./components/clients/PendingClientUpdates";
import ProcessClientUpdate from "./components/clients/ProcessClientUpdate";
import ClientMailer from "./components/clients/ClientMailer";
import ClientEditor from "./components/clients/ClientEditor";

// Document Components
import UploadIndex from "./components/documents/UploadIndex";
import DocumentIndex from "./components/documents/DocumentIndex";
import DocumentGenerator from "./components/documents/DocumentGenerator";
import PortalDocumentIndex from "./components/documents/PortalDocumentIndex";
import EngagementWatcher from "./components/documents/EngagementWatcher";
import EfileWatcher from "./components/documents/EfileWatcher";

import Upload8879s from "./components/documentUploader/Upload8879s";
import UploadUnsigned8879s from "./components/documentUploader/UploadUnsigned8879s";
import GenericUploader from "./components/documentUploader/GenericUploader";
import QRCode from "./components/documentUploader/QRCode";
import OrganizerUpload from "./components/documentUploader/OrganizerUpload";
import SourceDocumentUpload from "./components/documentUploader/SourceUpload";
import GenericPorter from "./components/documentUploader/GenericPorter";

// Template Components
import TemplateIndex from "./components/document_templates/TemplateIndex";
import TemplateView from "./components/document_templates/TemplateView";
import TemplateEditor from "./components/document_templates/TemplateEditor";

// Teamwork Components
import PeopleIndex from "./components/teamwork/people/PeopleIndex";
import PieView from "./components/teamwork/PieView";
import CompanyView from "./components/teamwork/companies/CompanyView";
import ProjectView from "./components/teamwork/projects/ProjectView";
import ProjectReports from "./components/teamwork/projects/ProjectReports";
import TeamworkCalendar from "./components/teamwork/scheduler/TeamworkCalendar";
import TeamworkScripts from "./components/teamwork/scripts/TeamworkScripts";

// Reports
import ReportGenerator from "./components/reports/ReportGenerator";
import ReportIndex from "./components/reports/ReportIndex";

// Assorted Components
import EmailEvents from "./components/Email/EmailEvents";
import EmailBuilder from "./components/Email/EmailBuilder";
import ClientNagIndex from "./components/Email/ClientNagIndex";
import ProcessClientNag from "./components/Email/ProcessClientNag";
import Stamps from "./components/Stamps/StampsLabelForm";
import ScrubSheet from "./components/IntegrityImplants/ScrubSheet";

//Payroll Components
import PayrollInput from "./components/Payroll/PayrollInput";
import EmailMatrixForm from "./components/Payroll/EmailMatrixForm";

//Company Components
import Company from "./components/companies/CompanyIndex";
import addEmployee from "./components/companies/AddEmployee";

//Home
import Home from "./components/Home";
import ShowPackageList from "./components/documentUploader/ShowPackageList";

//Administration
import Administration from "./components/Administration";

const Auth = UserAuthWrapper({
  authSelector: (state) => state.token.user,
  redirectAction: routerActions.replace,
  wrapperDisplayName: "Auth",
  failureRedirectPath: "/Login",
});

const router = (
  <Provider store={store}>
    <Router history={history}>
      <Route path="/" component={App}>
        <Route path="/Login" component={Login} />
        <Route path="/Home" component={Auth(Home)} />
        <Route path="job/salesForceClients" component={Auth(ClientIndex)} />
        <Route
          path="job/documentGenerator"
          component={Auth(DocumentGenerator)}
        />
        <Route path="job/emailEvents" component={Auth(EmailEvents)} />
        <Route path="job/Stamps" component={Auth(Stamps)} />
        <Route path="company/CompanyIndex" component={Auth(Company)} />
        <Route path="company/addEmployee/:_id" component={Auth(addEmployee)} />
        <Route path="GenericUploader" component={Auth(GenericUploader)} />
        <Route path="ShowPackageList" component={Auth(ShowPackageList)} />
        <Route path="QRCode" component={Auth(QRCode)} />
        <Route path="GenericPorter" component={Auth(GenericPorter)} />
        <Route path="Administration" component={Auth(Administration)} />
        <Route path="OrganizerUpload" component={Auth(OrganizerUpload)} />
        <Route
          path="SourceDocumentUpload"
          component={Auth(SourceDocumentUpload)}
        />
        <Route path="job/:_job">
          <Route path="ClientSyncIndex" component={Auth(ClientSyncIndex)} />
          <Route
            path="PendingClientUpdates"
            component={Auth(PendingClientUpdates)}
          />
          <Route
            path="ProcessClientUpdate/:_id"
            component={Auth(ProcessClientUpdate)}
          />
          <Route path="ClientEditor/:_id" component={Auth(ClientEditor)} />
          <Route path="upload8879s" component={Auth(Upload8879s)} />
          <Route
            path="uploadUnsigned8879s"
            component={Auth(UploadUnsigned8879s)}
          />
          <Route path="uploadHistory" component={Auth(UploadIndex)} />
          <Route path="temporaryDocuments" component={Auth(DocumentIndex)} />
          <Route path="portalDocuments" component={Auth(PortalDocumentIndex)} />
          {/*Document Watchers Routes*/}
          <Route path="engagementWatcher" component={Auth(EngagementWatcher)} />
          <Route path="eFileWatcher" component={Auth(EfileWatcher)} />
          {/*Communications Manager Routes*/}
          <Route path="clientMailer" component={Auth(ClientMailer)} />
          <Route path="massEmailer" component={Auth(EmailBuilder)} />
          <Route
            path="massEmailer/clientNagIndex"
            component={Auth(ClientNagIndex)}
          />
          <Route
            path="massEmailer/processClientNag/:_id"
            component={Auth(ProcessClientNag)}
          />
          {/*Document Template Routes*/}
          <Route path="documentTemplates" component={Auth(TemplateIndex)} />
          <Route
            path="documentTemplates/new"
            component={Auth(TemplateEditor)}
          />
          <Route
            path="documentTemplates/:_id"
            component={Auth(TemplateEditor)}
          />
          {/*Teamwork Routes*/}
          <Route path="pieChart" component={Auth(PieView)} />
          <Route path="people" component={Auth(PeopleIndex)} />
          <Route path="companies/:_id" component={Auth(CompanyView)} />
          <Route path="projects/:_id" component={Auth(ProjectView)} />
          <Route path="projectReports" component={Auth(ProjectReports)} />
          <Route path="teamworkCalendar" component={Auth(TeamworkCalendar)} />
          <Route path="teamworkScripts" component={Auth(TeamworkScripts)} />
          {/*Report Routes*/}
          <Route path="generate" component={Auth(ReportGenerator)} />
          <Route path="reports" component={Auth(ReportIndex)} />
          {/*Integrity Implants Routes*/}
          <Route path="scrubSheet" component={Auth(ScrubSheet)} />
          {/*Payroll routes*/}
          <Route path="PayrollInput" component={Auth(PayrollInput)} />
          <Route path="EmailMatrixForm" component={Auth(EmailMatrixForm)} />
        </Route>
        <Route path="*" component={NotFound} />
      </Route>
    </Router>
  </Provider>
);

render(router, document.getElementById("root"));



// WEBPACK FOOTER //
// ./app/Portal.js